/**
 * Carbon — personalisation line item properties on product pages.
 * Listens to Prestige variant:change on the product form.
 */
class CarbonPersonalisationFields extends HTMLElement {
  #form = null;
  #optionPosition = null;
  #leftInput = null;
  #rightInput = null;
  #errorEl = null;
  #maxlength = 12;
  #onVariantChange = null;
  #onOptionChange = null;
  #onRerender = null;
  #onSubmit = null;
  #onInput = null;

  connectedCallback() {
    const formId = this.getAttribute("form-id");

    if (!formId) {
      return;
    }

    this.#form = document.forms[formId];
    this.#optionPosition = this.dataset.optionPosition;
    this.#maxlength = Number.parseInt(this.dataset.maxlength || "12", 10);
    this.#leftInput = this.querySelector(
      'input[name="properties[Left side lettering]"]'
    );
    this.#rightInput = this.querySelector(
      'input[name="properties[Right side lettering]"]'
    );
    this.#errorEl = this.querySelector(".carbon-personalisation__error");

    if (!this.#form || !this.#leftInput || !this.#rightInput) {
      return;
    }

    this.#onVariantChange = () => {
      window.requestAnimationFrame(() => this.#toggleFromCheckedOption());
    };

    this.#onOptionChange = (event) => {
      if (
        !event.target.matches(
          `input[data-option-position="${this.#optionPosition}"]`
        )
      ) {
        return;
      }

      this.#toggleFromCheckedOption();
    };

    this.#onRerender = () => {
      window.requestAnimationFrame(() => this.#syncFromPicker());
    };

    this.#onSubmit = (event) => {
      if (this.hidden) {
        return;
      }

      this.#normalizeInputValue(this.#leftInput);
      this.#normalizeInputValue(this.#rightInput);

      const left = this.#leftInput.value.trim();
      const right = this.#rightInput.value.trim();

      if (!left && !right) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.#showError(
          this.dataset.errorRequired ||
            "Please enter your lettering on at least one side."
        );
        this.#leftInput.focus();
        return;
      }

      if (left && (!this.#leftInput.checkValidity() || !this.#isValidValue(left))) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.#showError(
          this.dataset.errorInvalid ||
            "Use letters, numbers, spaces, &, ., - and ' only."
        );
        this.#leftInput.focus();
        return;
      }

      if (right && (!this.#rightInput.checkValidity() || !this.#isValidValue(right))) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.#showError(
          this.dataset.errorInvalid ||
            "Use letters, numbers, spaces, &, ., - and ' only."
        );
        this.#rightInput.focus();
        return;
      }

      this.#omitEmptyPropertiesForSubmit(left, right);
    };

    this.#onInput = (event) => {
      this.#normalizeInputValue(event.target);
      this.#updateCharCount(event.target);
      this.#clearError();
    };

    this.#form.addEventListener("variant:change", this.#onVariantChange);
    this.#form.addEventListener("change", this.#onOptionChange);
    this.#form.addEventListener("product:rerender", this.#onRerender);
    this.#form.addEventListener("submit", this.#onSubmit, true);
    this.#leftInput.addEventListener("input", this.#onInput);
    this.#rightInput.addEventListener("input", this.#onInput);

    this.#syncFromPicker();
    this.#updateCharCounts();
  }

  disconnectedCallback() {
    if (!this.#form) {
      return;
    }

    this.#form.removeEventListener("variant:change", this.#onVariantChange);
    this.#form.removeEventListener("change", this.#onOptionChange);
    this.#form.removeEventListener("product:rerender", this.#onRerender);
    this.#form.removeEventListener("submit", this.#onSubmit, true);
    this.#leftInput?.removeEventListener("input", this.#onInput);
    this.#rightInput?.removeEventListener("input", this.#onInput);
  }

  #syncFromPicker() {
    this.#toggleFromCheckedOption();
  }

  #toggleFromCheckedOption() {
    const picker = this.closest("variant-picker");
    const checkedInput = picker?.querySelector(
      `input[data-option-position="${this.#optionPosition}"]:checked`
    );
    const label = checkedInput?.nextElementSibling?.textContent?.trim();

    this.#setVisible(label === "Personalised");
  }

  #setVisible(show) {
    if (show) {
      this.hidden = false;
      this.#leftInput.disabled = false;
      this.#rightInput.disabled = false;
      this.#updateCharCounts();
      return;
    }

    this.hidden = true;
    this.#leftInput.value = "";
    this.#rightInput.value = "";
    this.#leftInput.disabled = true;
    this.#rightInput.disabled = true;
    this.#updateCharCounts();
    this.#clearError();
  }

  #normalizeInputValue(input) {
    if (!input || input.disabled) {
      return;
    }

    const upper = input.value.toUpperCase();

    if (input.value === upper) {
      return;
    }

    const selectionStart = input.selectionStart;
    const selectionEnd = input.selectionEnd;

    input.value = upper;

    if (selectionStart !== null && selectionEnd !== null) {
      input.setSelectionRange(selectionStart, selectionEnd);
    }
  }

  #omitEmptyPropertiesForSubmit(left, right) {
    const toRestore = [];

    if (!left) {
      this.#leftInput.disabled = true;
      toRestore.push(this.#leftInput);
    }

    if (!right) {
      this.#rightInput.disabled = true;
      toRestore.push(this.#rightInput);
    }

    if (toRestore.length === 0) {
      return;
    }

    window.requestAnimationFrame(() => {
      if (this.hidden) {
        return;
      }

      toRestore.forEach((input) => {
        input.disabled = false;
      });
    });
  }

  #isValidValue(value) {
    return /^[\p{L}\p{N} &.''.\-]+$/u.test(value);
  }

  #updateCharCounts() {
    this.#updateCharCount(this.#leftInput);
    this.#updateCharCount(this.#rightInput);
  }

  #updateCharCount(input) {
    if (!input) {
      return;
    }

    const counter = this.querySelector(
      `[data-char-count-for="${input.id}"]`
    );
    const length = input.disabled ? 0 : input.value.length;

    if (counter) {
      counter.textContent = `${length}/${this.#maxlength}`;
    }
  }

  #showError(message) {
    if (!this.#errorEl) {
      return;
    }

    this.#errorEl.textContent = message;
    this.#errorEl.hidden = false;
  }

  #clearError() {
    if (!this.#errorEl) {
      return;
    }

    this.#errorEl.textContent = "";
    this.#errorEl.hidden = true;
  }
}

if (!window.customElements.get("carbon-personalisation-fields")) {
  window.customElements.define(
    "carbon-personalisation-fields",
    CarbonPersonalisationFields
  );
}
